import { useCallback, useState } from 'react'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import { useProjectMutations } from '@/lib/db/rxdb/hooks'
import { compareRanks, generateRankBetween } from '@/lib/utils/lexorank'
import { rebalanceSortOrders } from '@/lib/utils/sortOrder'

export type ProjectDropPosition = 'before' | 'after'

export type ProjectReorderEvent = {
  sourceId: string
  targetId: string
  dropPosition: ProjectDropPosition
}

function getSortedSiblings(
  projects: Array<ProjectDocument>,
  parentId: string | null,
): Array<ProjectDocument> {
  return projects
    .filter((p) => (p.parentProjectId || null) === parentId && !p.isDeleted)
    .sort((a, b) => {
      if (a.sortOrder && b.sortOrder) return a.sortOrder.localeCompare(b.sortOrder)
      if (a.sortOrder) return -1
      if (b.sortOrder) return 1
      return a.createdOn - b.createdOn
    })
}

/**
 * Reorder projects within a single parent space. v1 scope: same-parent only —
 * cross-parent drag is rejected. Mirrors usePageReorder.ts but without the
 * 'child' drop position or depth limits.
 */
export function useProjectReorder(projects: Array<ProjectDocument>) {
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [dropIndicator, setDropIndicator] = useState<{
    id: string
    position: ProjectDropPosition
  } | null>(null)

  const { updateProject } = useProjectMutations()

  const handleReorder = useCallback(
    (event: ProjectReorderEvent) => {
      if (event.sourceId === event.targetId) return

      const source = projects.find((p) => p.id === event.sourceId)
      const target = projects.find((p) => p.id === event.targetId)
      if (!source || !target) return

      // v1: reject cross-parent drags. The sidebar only registers drop targets
      // within the same parent, but guard anyway in case that changes.
      const sourceParent = source.parentProjectId || null
      const targetParent = target.parentProjectId || null
      if (sourceParent !== targetParent) return

      const siblings = getSortedSiblings(projects, targetParent)
      const reordered = siblings.filter((p) => p.id !== event.sourceId)
      const targetIndex = reordered.findIndex((p) => p.id === event.targetId)
      if (targetIndex === -1) return

      const insertIndex =
        event.dropPosition === 'after' ? targetIndex + 1 : targetIndex
      reordered.splice(insertIndex, 0, source)

      const movedIndex = reordered.findIndex((p) => p.id === source.id)
      const beforeSort =
        movedIndex > 0 ? (reordered[movedIndex - 1]?.sortOrder ?? null) : null
      const afterSort =
        movedIndex < reordered.length - 1
          ? (reordered[movedIndex + 1]?.sortOrder ?? null)
          : null

      const hasNullSortOrders = reordered.some((item) => !item.sortOrder)
      const shouldRebalance =
        hasNullSortOrders ||
        (beforeSort !== null &&
          afterSort !== null &&
          compareRanks(beforeSort, afterSort) >= 0)

      if (shouldRebalance) {
        void rebalanceSortOrders(reordered, async (id, updates) => {
          await updateProject(id, updates)
        })
        return
      }

      const nextSortOrder = generateRankBetween(beforeSort, afterSort)
      if (nextSortOrder === source.sortOrder) return

      void updateProject(event.sourceId, { sortOrder: nextSortOrder })
    },
    [projects, updateProject],
  )

  return {
    handleReorder,
    draggingId,
    setDraggingId,
    dropIndicator,
    setDropIndicator,
  }
}
